import { get, forEach } from 'lodash';

import { t } from '@/plugins/i18n';
import { getAllPlanPrice$Cache } from '../../datas/pricingMap';
import { getStoreCurrency, isZpUnavailableCurrency } from '@/components/ADDON_LEARN_MORE_DIALOG/money';
import { USER_LANG } from '@zoom/billing-kit/src/constant/language';

export const unlockProLicenseCount = get(window, 'campaignFlags.enableNewLicenseLimit.pro', false);

export const unlockBizLicenseCount = get(window, 'campaignFlags.enableNewLicenseLimit.bizAndBizPlus', false);

// NOTE: The feature toggle was already enabled in 2024, and this component is also applied to other teams simultaneously. Therefore, the default value here is modified to "true"
export const unlockBizMaxLicenseCount = get(window, 'campaignFlags.enableNewLicenseLimit.maxBizAndBizPlus', true);

export const minBizAndBizPlusLicenseCount = unlockBizLicenseCount ? 1 : 10;
export const maxBizAndBizPlusLicenseCount = unlockBizMaxLicenseCount ? 250 : 99;
export const bizAndBizPlusLicenseInterval = `${minBizAndBizPlusLicenseCount}-${maxBizAndBizPlusLicenseCount}`;

export const enableLiveChatSupport = get(window, 'campaignFlags.enableLiveChatSupport', false);

export const PBX_REGIONS = {
  US_AND_CANADA: 'US',
  AUS_AND_NZ: 'AU',
  UK_AND_IRELAND: 'UK',
  UK: 'GB', // United Kingdom
  IE: 'IE', // Ireland
  GLOBAL: 'NZ', // New Zealand
  JP: 'JP',
};

export const PLAN_CATEGORY_CODES = {
  PRO: 'PRO',
  BUSINESS: 'BIZ',
  CLOUD_STORAGE: 'CMR',
  WEBINAR: 'WBN',
  LARGE_MEETING: 'LMT',
  WHITEBOARD: 'ZWB',
  TRANSLATED_CAPTIONS: 'ZTRANSL',
  ZOOM_SCHEDULER: 'ZSCHED',
  ZOOM_EVENTS: 'ZET',
  ZOOM_SESSIONS: 'ZETZEC',
  ZOOM_ROOMS: 'ZRM',
  TOLLFREE: 'ADR',
  ZOOM_PHONE: 'ZMV',
  CONFERENCE_ROOM_CONNECTOR: 'RCN',
  ZOOM_ONE_BIZ_PLUS: 'ZONEBIZP',
  ZOOM_ONE_PRO_PLUS: 'ZONEPROP',
  ZOOM_CLIPS_PLUS: 'ZCLIPS',
  BASIC_PLUS: 'ZOBASICP',
  LDKCMR: 'LDKCMR',
  ZOOM_REVENUE_ACCELERATOR: 'ZKW',
  CONCURRENT_MEETING_BASIC: 'CCM',
  ZCM: 'ZCMPM',
  ZOOM_STA_DOC: 'ZDOCS',
  ENTERPIRSE_NAMED_HOST: 'ENTBD',
  ZOOM_ONE_EDU: 'ZONEDU',
  PSO: 'PSO',
  VISITOR_MANAGEMENT: 'ZVSTMGT',
  WORKSPACE_RESERVATION: 'ZWR',
};

export const PLAN_TYPES = {
  LARGE_MEETING: 'large',
  LARGE_MEETING_1000: 'large1000',
  ZOOM_CLIPS_PLUS: 'zclips',
  ZOOM_ONE_PRO: 'pro',
  ZOOM_ONE_PRO_PLUS: 'zoneproplus',
  ZOOM_ONE_BIZ: 'biz',
  ZOOM_ONE_BIZ_PLUS: 'zone_bizplus',
  ZOOM_UNITED: 'zoomunited',
  EVENT_SAAS: 'eventsaas',
  EVENT_SAAS_CORE: 'eventsaascore',
  WEBINAR: 'webinar',
  VISITOR_MANAGEMENT: 'zvstmgt',
  WORKSPACE_RESERVATION: 'workspace_reservation',
  CUSTOM_AVATAR: 'custom_avatar'
};

export const PLAN_UNIFIED_LINKS = {
  LARGE_MEETING: '/buy?product=LMT',
  ZOOM_CLIPS_PLUS: '/buy?product=ZCLIPSPLUS',
  ZOOM_ONE_PRO: '/buy?product=PRO',
  ZOOM_ONE_PRO_PLUS: '/buy?product=ZONEPROPZVU',
  ZOOM_ONE_BIZ: '/buy?product=BIZ',
  ZOOM_ONE_BIZ_PLUS: '/buy?product=ZONEBIZPZVU',
  ZOOM_ONE_BIZ_PLUS__ZAU: '/buy?product=ZONEBIZPZAU',
  ZOOM_ONE_BIZ_PLUS__ZJU: '/buy?product=ZONEBIZPZJU',
  ZOOM_ONE_BIZ_PLUS__ZUU: '/buy?product=ZONEBIZPZUU',
  EVENT_SAAS_UNLIMITED: '/buy?product=ZESU',
  EVENT_SAAS_PPA: '/buy?product=ZECO',
  EVENT_SAAS_CORE_UNLIMITED: '/buy?product=ZECSU',
  EVENT_SAAS_CORE_PPA: '/buy?product=ZECCO',
};

export const PLAN_TYPES_WITH_CAPACITY = {
  EVENT_SAAS_100: 'eventsaas100',
  EVENT_SAAS_500: 'eventsaas500',
  EVENT_SAAS_1000: 'eventsaas1000',
  EVENT_SAAS_3000: 'eventsaas3000',
  EVENT_SAAS_CORE_100: 'eventsaascore100',
  EVENT_SAAS_CORE_500: 'eventsaascore500',
  EVENT_SAAS_CORE_1000: 'eventsaascore1000',
  EVENT_SAAS_CORE_3000: 'eventsaascore3000',
  WEBINAR_500: 'webinar500',
  WEBINAR_1000: 'webinar1000',
  WEBINAR_3000: 'webinar3000',
  WEBINAR_5000: 'webinar5000',
  WEBINAR_10000: 'webinar10000',
};

export const ZOOM_UNITED_CATEGORY_CODE = {
  PRO: 'UCPRO',
  BIZ: 'UCBIZ',
};

const PricingKeyMapper = {
  [PLAN_TYPES.LARGE_MEETING]: {
    monthlyPriceKey: 'large500_monthly',
    annualPriceKey: 'large500_yearly',
  },
  [PLAN_TYPES.LARGE_MEETING_1000]: {
    monthlyPriceKey: 'PLANCODE_1508',
    annualPriceKey: 'PLANCODE_1509',
  },
  [PLAN_TYPES.ZOOM_CLIPS_PLUS]: {
    monthlyPriceKey: 'zm_clips_plus_monthly',
    annualPriceKey: 'zm_clips_plus_yearly',
  },
  [PLAN_TYPES.ZOOM_ONE_PRO]: {
    monthlyPriceKey: ['zo_pro_monthly', 'monthly_c50'],
    annualPriceKey: ['zo_pro_yearly', 'yearly_c50'],
  },
  [PLAN_TYPES.ZOOM_ONE_PRO_PLUS]: {
    monthlyPriceKey: 'zwpp_us_ca_monthly',
    annualPriceKey: 'zwpp_us_ca_yearly',
  },
  [PLAN_TYPES.ZOOM_ONE_BIZ]: {
    monthlyPriceKey: 'business_monthly_c50',
    annualPriceKey: 'business_yearly_c50',
  },
  [PLAN_TYPES.ZOOM_ONE_BIZ_PLUS]: {
    monthlyPriceKey: 'zonebizpzvu_monthly',
    annualPriceKey: 'zonebizpzvu_yearly',
  },
  [PLAN_TYPES.WEBINAR]: {
    monthlyPriceKey: 'webinar500_monthly',
    annualPriceKey: 'webinar500_yearly',
  },
  [PLAN_TYPES_WITH_CAPACITY.WEBINAR_1000]: {
    monthlyPriceKey: 'webinar1000_monthly',
    annualPriceKey: 'webinar1000_yearly',
  },
  [PLAN_TYPES_WITH_CAPACITY.WEBINAR_3000]: {
    monthlyPriceKey: 'webinar3000_monthly',
    annualPriceKey: 'webinar3000_yearly',
  },
  [PLAN_TYPES_WITH_CAPACITY.WEBINAR_5000]: {
    monthlyPriceKey: 'webinar5000_monthly',
  },
  [PLAN_TYPES_WITH_CAPACITY.WEBINAR_10000]: {
    monthlyPriceKey: 'webinar10000_monthly',
  },
  [PLAN_TYPES.EVENT_SAAS]: {
    monthlyPriceKey: 'ze_100_unlimited_monthly',
    annualPriceKey: 'ze_100_unlimited_yearly',
  },
  [PLAN_TYPES_WITH_CAPACITY.EVENT_SAAS_500]: {
    annualPriceKey: 'ze_500_unlimited_yearly',
  },
  [PLAN_TYPES_WITH_CAPACITY.EVENT_SAAS_1000]: {
    annualPriceKey: 'ze_1000_unlimited_yearly',
  },
  [PLAN_TYPES_WITH_CAPACITY.EVENT_SAAS_3000]: {
    annualPriceKey: 'ze_3000_unlimited_yearly',
  },
  [PLAN_TYPES.EVENT_SAAS_CORE]: {
    monthlyPriceKey: 'zec_100_unlimited_monthly',
    annualPriceKey: 'zec_100_unlimited_yearly',
  },
  [PLAN_TYPES_WITH_CAPACITY.EVENT_SAAS_CORE_500]: {
    annualPriceKey: 'zec_500_unlimited_yearly',
  },
  [PLAN_TYPES_WITH_CAPACITY.EVENT_SAAS_CORE_1000]: {
    annualPriceKey: 'zec_1000_unlimited_yearly',
  },
  [PLAN_TYPES_WITH_CAPACITY.EVENT_SAAS_CORE_3000]: {
    annualPriceKey: 'zec_3000_unlimited_yearly',
  },
  [PLAN_TYPES.VISITOR_MANAGEMENT]: {
    monthlyPriceKey: 'zm_visitor_management_monthly',
    annualPriceKey: 'zm_visitor_management_yearly',
  },
  [PLAN_TYPES.WORKSPACE_RESERVATION]: {
    monthlyPriceKey: 'workspace_reservation_monthly',
    annualPriceKey: 'workspace_reservation_annual',
  },
};

const getZoomOneBizBuyUrlByIP = () => {
  const currentCountry = get(window.campaignFlags, 'currentCountry', 'US');
  switch (currentCountry) {
    case PBX_REGIONS.UK_AND_IRELAND:
    case PBX_REGIONS.UK:
    case PBX_REGIONS.IE:
      return PLAN_UNIFIED_LINKS.ZOOM_ONE_BIZ_PLUS__ZUU;
    case PBX_REGIONS.AUS_AND_NZ:
    case PBX_REGIONS.GLOBAL:
      return PLAN_UNIFIED_LINKS.ZOOM_ONE_BIZ_PLUS__ZAU;
    case PBX_REGIONS.JP:
      return PLAN_UNIFIED_LINKS.ZOOM_ONE_BIZ_PLUS__ZJU;
    default:
      return PLAN_UNIFIED_LINKS.ZOOM_ONE_BIZ_PLUS;
  }
}

const getZoomOneBizPlusIntroListByIP = () => {
  const currentCountry = get(window.campaignFlags, 'currentCountry', 'US');
  switch (currentCountry) {
    case PBX_REGIONS.UK_AND_IRELAND:
    case PBX_REGIONS.UK:
    case PBX_REGIONS.IE:
      return [
        {
          label: t('fbc.add_ons.zoom_one.intro_label8_UK'),
          tip: t('fbc.add_ons.zoom_one.intro_label_tip2'),
          tipLabel: 'Learn more about Zoom Phone UK & Ireland Unlimited',
        },
        { label: t('fbc.add_ons.zoom_one.intro_label9') },
        {
          label: t('fbc.add_ons.zoom_one.intro_label10'),
          tip: t('fbc.add_ons.zoom_one.intro_label_tip3'),
          tipLabel: 'Learn more about Additional features',
        },
      ];
    case PBX_REGIONS.AUS_AND_NZ:
    case PBX_REGIONS.GLOBAL:
      return [
        {
          label: t('fbc.add_ons.zoom_one.intro_label8_AU'),
          tip: t('fbc.add_ons.zoom_one.intro_label_tip2'),
          tipLabel: 'Learn more about Zoom Phone Australia & NZ Unlimited',
        },
        { label: t('fbc.add_ons.zoom_one.intro_label9') },
        {
          label: t('fbc.add_ons.zoom_one.intro_label10'),
          tip: t('fbc.add_ons.zoom_one.intro_label_tip3'),
          tipLabel: 'Learn more about Additional features',
        },
      ];
    case PBX_REGIONS.JP:
      return [
        {
          label: t('fbc.add_ons.zoom_one.intro_label8_JP'),
          tip: t('fbc.add_ons.zoom_one.intro_label_tip2'),
          tipLabel: 'Learn more about Zoom Phone Japan Unlimited',
        },
        { label: t('fbc.add_ons.zoom_one.intro_label9') },
        {
          label: t('fbc.add_ons.zoom_one.intro_label10'),
          tip: t('fbc.add_ons.zoom_one.intro_label_tip3'),
          tipLabel: 'Learn more about Additional features',
        },
      ];
    default:
      return [
        {
          label: t('fbc.add_ons.zoom_one.intro_label8'),
          tip: t('fbc.add_ons.zoom_one.intro_label_tip2'),
          tipLabel: 'Learn more about Zoom Phone US & CA Unlimited',
        },
        { label: t('fbc.add_ons.zoom_one.intro_label9') },
        {
          label: t('fbc.add_ons.zoom_one.intro_label10'),
          tip: t('fbc.add_ons.zoom_one.intro_label_tip3'),
          tipLabel: 'Learn more about Additional features',
        },
      ];
  }
};

export const LEARN_MORE_PLANS = [
  {
    type: PLAN_TYPES.LARGE_MEETING,
    parent_category: PLAN_CATEGORY_CODES.LARGE_MEETING,
    buy_url: PLAN_UNIFIED_LINKS.LARGE_MEETING + '&capacity=500',
    poster: require('@/assets/images/addonBackground/addon-large-meetings.png'),
    icon: 'large-meeting',
    title: t('fbc.plans.large_meeting.title'),
    dependence: t('fbc.add_ons.zoom_one_required'),
    summary: t('fbc.add_ons.large.features_tip'),
    optionList: [
      { value: PLAN_TYPES.LARGE_MEETING, labelTitle: t('fbc.common.statistics.participants', ['500']) },
      { value: PLAN_TYPES.LARGE_MEETING_1000, labelTitle: t('fbc.common.statistics.participants', ['1,000']) },
    ],
    purchaseUnit: 'fbc.add_ons.unit.license',
    introList: [
      { label: t('fbc.plans.large_meeting.description1') },
      { label: t('fbc.plans.large_meeting.description2') },
    ],
  },
  {
    type: PLAN_TYPES.LARGE_MEETING_1000,
    buy_url: PLAN_UNIFIED_LINKS.LARGE_MEETING + '&capacity=1000',
  },
  {
    type: PLAN_TYPES.ZOOM_CLIPS_PLUS,
    parent_category: PLAN_CATEGORY_CODES.ZOOM_CLIPS_PLUS,
    buy_url: PLAN_UNIFIED_LINKS.ZOOM_CLIPS_PLUS,
    poster: require('@/assets/images/addonBackground/poster-clip-bg.png'),
    // videoSrc: 'https://youtu.be/7h5T8yItJR4?si=Z6on6QeG8x8Dl4Pa',
    icon: 'zoom-clips',
    title: t('fbc.plans.clips_plus.title'),
    summary: t('fbc.add_ons.zoom_clips_plus.features_tip'),
    purchaseUnit: 'fbc.add_ons.unit.license',
    introList: [
      { label: t('fbc.add_ons.zoom_clips_plus.intro_label1') },
      { label: t('fbc.add_ons.zoom_clips_plus.intro_label2') },
      { label: t('fbc.add_ons.zoom_clips_plus.intro_label3') },
      { label: t('fbc.add_ons.zoom_clips_plus.intro_label4') },
    ],
    explainDesc: t('fbc.add_ons.zoom_clips_plus.explain_desc'),
  },
  {
    type: PLAN_TYPES.ZOOM_ONE_PRO,
    parent_category: PLAN_CATEGORY_CODES.PRO,
    buy_url: PLAN_UNIFIED_LINKS.ZOOM_ONE_PRO,
    poster: require('@/assets/images/addonBackground/poster-zoom-workplace.png'),
    // videoSrc: 'https://www.youtube.com/watch?v=Gp_Q6XrNWSU',
    icon: 'zoom-one',
    title: t('fbc.add_ons.zoom_one.title'),
    summary: t('fbc.add_ons.zoom_one.features_tip'),
    purchaseUnit: 'fbc.add_ons.unit.user',
    optionList: [
      {
        value: PLAN_TYPES.ZOOM_ONE_PRO,
        labelTitle: t('fbc.add_ons.zoom_one.label_title1'),
        labelDesc: t('fbc.common.statistics.user_range', ['1', unlockProLicenseCount ? '99' : '9']),
      },
      {
        value: PLAN_TYPES.ZOOM_ONE_PRO_PLUS,
        labelTitle: t('fbc.add_ons.zoom_one.label_title2'),
        labelDesc: t('fbc.common.statistics.user_range', ['1', '99']),
      },
      {
        value: PLAN_TYPES.ZOOM_ONE_BIZ,
        labelTitle: t('fbc.add_ons.zoom_one.label_title3'),
        labelDesc: t('fbc.common.statistics.user_range', [minBizAndBizPlusLicenseCount, maxBizAndBizPlusLicenseCount]),
      },
      {
        value: PLAN_TYPES.ZOOM_ONE_BIZ_PLUS,
        labelTitle: t('fbc.add_ons.zoom_one.label_title4'),
        labelDesc: t('fbc.common.statistics.user_range', [minBizAndBizPlusLicenseCount, maxBizAndBizPlusLicenseCount]),
      },
    ],
    introTitle: '',
    introList: [
      { label: t('fbc.add_ons.zoom_one.intro_label1') },
      { label: t('fbc.add_ons.zoom_one.intro_label2') },
      { label: t('fbc.add_ons.zoom_one.intro_label3') },
      { label: t('fbc.add_ons.zoom_one.intro_label4') },
    ].concat(enableLiveChatSupport ? [{ label: t('fbc.add_ons.zoom_one.intro_label11') }] : []),
  },
  {
    type: PLAN_TYPES.ZOOM_ONE_PRO_PLUS,
    parent_category: PLAN_CATEGORY_CODES.ZOOM_ONE_PRO_PLUS,
    buy_url: PLAN_UNIFIED_LINKS.ZOOM_ONE_PRO_PLUS,
    introTitle: t('fbc.add_ons.zoom_one.intro_title1'),
    introList: [
      {
        label: t('fbc.add_ons.zoom_one.intro_label5'),
        tip: t('fbc.add_ons.zoom_one.intro_label_tip4'),
        tipLabel: 'Learn more about Zoom Phone US & CA Unlimited',
      },
    ],
  },
  {
    type: PLAN_TYPES.ZOOM_ONE_BIZ,
    parent_category: PLAN_CATEGORY_CODES.BUSINESS,
    buy_url: PLAN_UNIFIED_LINKS.ZOOM_ONE_BIZ,
    introTitle: t('fbc.add_ons.zoom_one.intro_title1'),
    introList: [{ label: t('fbc.add_ons.zoom_one.intro_label6') }, { label: t('fbc.add_ons.zoom_one.intro_label7') }],
  },
  {
    type: PLAN_TYPES.ZOOM_ONE_BIZ_PLUS,
    parent_category: PLAN_CATEGORY_CODES.ZOOM_ONE_BIZ_PLUS,
    buy_url: getZoomOneBizBuyUrlByIP(),
    introTitle: t('fbc.add_ons.zoom_one.intro_title2'),
    introList: getZoomOneBizPlusIntroListByIP(),
  },
  {
    type: PLAN_TYPES.EVENT_SAAS,
    parent_category: PLAN_CATEGORY_CODES.ZOOM_EVENTS,
    buy_url: PLAN_UNIFIED_LINKS.EVENT_SAAS_UNLIMITED + '&capacity=100',
    poster: require('@/assets/images/addonBackground/addon-eventsaas.png'),
    icon: 'zoom-events',
    title: t('fbc.add_ons.eventsaas.title'),
    dependence: t('fbc.add_ons.zoom_one_required'),
    summary: t('fbc.add_ons.eventsaas.features_tip'),
    tooltipContent: t('fbc.add_ons.eventsaas.tooltip_content'),
    optionList: [
      {
        value: PLAN_TYPES.EVENT_SAAS,
        labelTitle: t('fbc.common.statistics.attendees', ['100']),
      },
      {
        value: PLAN_TYPES_WITH_CAPACITY.EVENT_SAAS_500,
        labelTitle: t('fbc.common.statistics.attendees', ['500']),
      },
      {
        value: PLAN_TYPES_WITH_CAPACITY.EVENT_SAAS_1000,
        labelTitle: t('fbc.common.statistics.attendees', ['1,000']),
      },
      {
        value: PLAN_TYPES_WITH_CAPACITY.EVENT_SAAS_3000,
        labelTitle: t('fbc.common.statistics.attendees', ['3,000']),
      },
    ],
    introTitle: t('fbc.add_ons.eventsaas.intro_title'),
    introList: [
      { label: t('fbc.add_ons.eventsaas.intro_label1') },
      { label: t('fbc.add_ons.eventsaas.intro_label2') },
      { label: t('fbc.add_ons.eventsaas.intro_label3') },
      { label: t('fbc.add_ons.eventsaas.intro_label4') },
      { label: t('fbc.add_ons.eventsaas.intro_label5') },
      { label: t('fbc.add_ons.eventsaas.intro_label6') },
    ],
  },
  {
    type: PLAN_TYPES_WITH_CAPACITY.EVENT_SAAS_500,
    buy_url: PLAN_UNIFIED_LINKS.EVENT_SAAS_UNLIMITED + '&capacity=500',
  },
  {
    type: PLAN_TYPES_WITH_CAPACITY.EVENT_SAAS_1000,
    buy_url: PLAN_UNIFIED_LINKS.EVENT_SAAS_UNLIMITED + '&capacity=1000',
  },
  {
    type: PLAN_TYPES_WITH_CAPACITY.EVENT_SAAS_3000,
    buy_url: PLAN_UNIFIED_LINKS.EVENT_SAAS_UNLIMITED + '&capacity=3000',
  },
  {
    type: PLAN_TYPES.EVENT_SAAS_CORE,
    parent_category: PLAN_CATEGORY_CODES.ZOOM_SESSIONS,
    buy_url: PLAN_UNIFIED_LINKS.EVENT_SAAS_CORE_UNLIMITED + '&capacity=100',
    poster: require('@/assets/images/addonBackground/addon-eventsaascore.png'),
    icon: 'zoom-sessions',
    title: t('fbc.add_ons.eventsaascore.title'),
    dependence: t('fbc.add_ons.zoom_one_required'),
    summary: t('fbc.add_ons.eventsaascore.features_tip'),
    tooltipContent: t('fbc.add_ons.eventsaascore.tooltip_content'),
    optionList: [
      {
        value: PLAN_TYPES.EVENT_SAAS_CORE,
        labelTitle: t('fbc.common.statistics.attendees', ['100']),
      },
      {
        value: PLAN_TYPES_WITH_CAPACITY.EVENT_SAAS_CORE_500,
        labelTitle: t('fbc.common.statistics.attendees', ['500']),
      },
      {
        value: PLAN_TYPES_WITH_CAPACITY.EVENT_SAAS_CORE_1000,
        labelTitle: t('fbc.common.statistics.attendees', ['1,000']),
      },
      {
        value: PLAN_TYPES_WITH_CAPACITY.EVENT_SAAS_CORE_3000,
        labelTitle: t('fbc.common.statistics.attendees', ['3,000']),
      },
    ],
    introTitle: t('fbc.add_ons.eventsaascore.intro_title'),
    introList: [
      { label: t('fbc.add_ons.eventsaascore.intro_label1') },
      { label: t('fbc.add_ons.eventsaascore.intro_label2') },
      { label: t('fbc.add_ons.eventsaascore.intro_label3') },
      { label: t('fbc.add_ons.eventsaascore.intro_label4') },
      { label: t('fbc.add_ons.eventsaascore.intro_label5') },
    ],
  },
  {
    type: PLAN_TYPES_WITH_CAPACITY.EVENT_SAAS_CORE_500,
    buy_url: PLAN_UNIFIED_LINKS.EVENT_SAAS_CORE_UNLIMITED + '&capacity=500',
  },
  {
    type: PLAN_TYPES_WITH_CAPACITY.EVENT_SAAS_CORE_1000,
    buy_url: PLAN_UNIFIED_LINKS.EVENT_SAAS_CORE_UNLIMITED + '&capacity=1000',
  },
  {
    type: PLAN_TYPES_WITH_CAPACITY.EVENT_SAAS_CORE_3000,
    buy_url: PLAN_UNIFIED_LINKS.EVENT_SAAS_CORE_UNLIMITED + '&capacity=3000',
  },
  {
    type: PLAN_TYPES.WEBINAR,
    parent_category: PLAN_CATEGORY_CODES.WEBINAR,
    poster: require('@/assets/images/addonBackground/poster-webinar-bg.png'),
    // videoSrc: 'https://youtu.be/shqx1bZDLvY?si=7O_v2RmLkMFQg7BE',
    icon: 'zoom-webinars',
    title: t('fbc.add_ons.webinar.title'),
    dependence: t('fbc.add_ons.zoom_one_required'),
    summary: t('fbc.add_ons.webinar.features_tip'),
    tooltipContent: t('fbc.add_ons.webinar.tooltip_content'),
    optionList: [
      {
        value: PLAN_TYPES.WEBINAR,
        labelTitle: t('fbc.common.statistics.attendees', ['500']),
      },
      {
        value: PLAN_TYPES_WITH_CAPACITY.WEBINAR_1000,
        labelTitle: t('fbc.common.statistics.attendees', ['1,000']),
      },
      {
        value: PLAN_TYPES_WITH_CAPACITY.WEBINAR_3000,
        labelTitle: t('fbc.common.statistics.attendees', ['3,000']),
      },
      {
        value: PLAN_TYPES_WITH_CAPACITY.WEBINAR_5000,
        labelTitle: t('fbc.common.statistics.attendees', ['5,000']),
      },
      {
        value: PLAN_TYPES_WITH_CAPACITY.WEBINAR_10000,
        labelTitle: t('fbc.common.statistics.attendees', ['10,000']),
      },
    ],
    introList: [
      { label: t('fbc.add_ons.webinar.intro_label1') },
      { label: t('fbc.add_ons.webinar.intro_label2') },
      { label: t('fbc.add_ons.webinar.intro_label3') },
      { label: t('fbc.add_ons.webinar.intro_label4') },
    ],
  },
  {
    type: PLAN_TYPES_WITH_CAPACITY.WEBINAR_500,
  },
  {
    type: PLAN_TYPES_WITH_CAPACITY.WEBINAR_1000,
  },
  {
    type: PLAN_TYPES_WITH_CAPACITY.WEBINAR_3000,
  },
  {
    type: PLAN_TYPES_WITH_CAPACITY.WEBINAR_5000,
  },
  {
    type: PLAN_TYPES_WITH_CAPACITY.WEBINAR_10000,
  },
  {
    type: PLAN_TYPES.WORKSPACE_RESERVATION,
    buy_url: PLAN_TYPES.WORKSPACE_RESERVATION,
    parent_category: PLAN_CATEGORY_CODES.WORKSPACE_RESERVATION,
    icon: 'workspace_reservation',
    title: t('fbc.add_ons.workspace_reservation.title'),
    poster: require('@/assets/images/addonBackground/addon-workspace-reservation.png'),
    videoSrc: 'https://st1.zoom.us/billingplanintro/Zoom_Workspace_Reservations_v11.mp4',
    summary: t('fbc.add_ons.workspace_reservation.intro_title'),
    monthlyLicense: t('per_billing_cycle_space.monthly'),
    annuallyLicense: t('per_billing_cycle_space.yearly'),
    introList: [{ label: t('fbc.add_ons.workspace_reservation.intro_label1') }, { label: t('fbc.add_ons.workspace_reservation.intro_label2') }, { label: t('fbc.add_ons.workspace_reservation.intro_label3') }, { label: t('fbc.add_ons.workspace_reservation.intro_label4') }],
  },
  {
    type: PLAN_TYPES.VISITOR_MANAGEMENT,
    buy_url: PLAN_TYPES.VISITOR_MANAGEMENT,
    parent_category: PLAN_CATEGORY_CODES.VISITOR_MANAGEMENT,
    icon: 'visitor_management',
    title: t('fbc.add_ons.visitor_management.title'),
    poster: require('@/assets/images/addonBackground/addon-visitor-management.png'),
    videoSrc: 'https://st1.zoom.us/billingplanintro/visitorManager.94effa35.mp4',
    summary: t('fbc.add_ons.visitor_management.intro_title'),
    monthlyLicense: t('per_billing_cycle_user.monthly'),
    annuallyLicense: t('per_billing_cycle_user.annually'),
    introList: [{ label: t('fbc.add_ons.visitor_management.intro_label1') }, { label: t('fbc.add_ons.visitor_management.intro_label2') }, { label: t('fbc.add_ons.visitor_management.intro_label3') }, { label: t('fbc.add_ons.visitor_management.intro_label4') }],
  },
  {
    type: PLAN_TYPES.CUSTOM_AVATAR,
    icon: 'custom_avatar',
    title: t('fbc.plan_details.custom_avatar.title'),
    poster: require('@/assets/images/addonBackground/addon-custom_avatar.png'),
    videoSrc: '',
    buttonComponent: () => null, // Will be update in 2025 May release
    tag: t('fbc.common.tag.new'), // Will be remove in later release
    summary: t('fbc.plan_details.custom_avatar.learn_more_subtitle'),
    introList: [{ label: t('fbc.plan_details.custom_avatar.features_item1') }, { label: t('fbc.plan_details.custom_avatar.features_item2') }, { label: t('fbc.plan_details.custom_avatar.features_item3') }, { label: t('fbc.plan_details.custom_avatar.features_item4') }],
  },
];

export const getAllPrices = async () => {
  const priceJson = await getAllPlanPrice$Cache();
  const currency = getStoreCurrency();
  return getAttendeeAreaPriceResult(priceJson)[currency];
};

export const getPriceAmountByKey = (allPrices, priceKey) => {
  let price = 0;
  if (Array.isArray(priceKey)) {
    for (let i = 0; i < priceKey.length; i++) {
      const key = priceKey[i];
      const _price = get(allPrices, key, 0);
      if (!price && _price) {
        price = _price;
        break;
      }
    }
  } else {
    price = get(allPrices, priceKey, 0);
  }

  return price;
};

export const getLearnMoreDialogPlans = async () => {
  const allPrices = await getAllPrices();

  return LEARN_MORE_PLANS.map((plan) => {
    if (!plan.monthlyPrice) {
      const monthlyPriceKey = PricingKeyMapper[plan.type]?.monthlyPriceKey;
      plan.monthlyPrice = getPriceAmountByKey(allPrices, monthlyPriceKey);
    }

    if (!plan.annualPrice) {
      const annualPriceKey = PricingKeyMapper[plan.type]?.annualPriceKey;
      plan.annualPrice = getPriceAmountByKey(allPrices, annualPriceKey);
    }

    if (plan.type === PLAN_TYPES.ZOOM_ONE_PRO) {
      if (isZpUnavailableCurrency()) {
        if (plan.type === PLAN_TYPES.ZOOM_ONE_PRO) {
          plan.optionList = plan.optionList.filter((opt) =>
            [PLAN_TYPES.ZOOM_ONE_PRO, PLAN_TYPES.ZOOM_ONE_BIZ].includes(opt.value),
          );
        }
      }

      // 20240913: The current dialog is only displayed to free accounts, so there is no need to consider united accounts for now.
      // if (basePlanInfo?.type === PLAN_TYPES.ZOOM_UNITED) {
      //   if (basePlanInfo.parentCategoryCode === ZOOM_UNITED_CATEGORY_CODE.PRO) {
      //     plan.optionList = plan.optionList.filter((opt) => opt.value === PLAN_TYPES.ZOOM_ONE_PRO);
      //   } else if (basePlanInfo.parentCategoryCode === ZOOM_UNITED_CATEGORY_CODE.BIZ) {
      //     plan.optionList = plan.optionList.filter((opt) => opt.value === PLAN_TYPES.ZOOM_ONE_BIZ);
      //   }
      // }
    }

    // 20240913: The current dialog is only displayed to free accounts, so there is no need to consider united accounts for now.
    // if (plan.type === PLAN_TYPES.ZOOM_ONE_BIZ) {
    //   if (basePlanInfo?.type === PLAN_TYPES.ZOOM_UNITED) {
    //     if (basePlanInfo.parentCategoryCode === ZOOM_UNITED_CATEGORY_CODE.BIZ) {
    //       plan.introTitle = '';
    //       plan.introList = [
    //         { label: t('fbc.add_ons.zoom_one.intro_label2') },
    //         { label: t('fbc.add_ons.zoom_one.intro_label3') },
    //         { label: t('fbc.add_ons.zoom_one.intro_label4') },
    //         { label: t('fbc.add_ons.zoom_one.intro_label6') },
    //         { label: t('fbc.add_ons.zoom_one.intro_label7') },
    //       ];
    //     }
    //   }
    // }

    return plan;
  });
};

// Reference: fe-pricing/src/mixins/webinarMixin.js -> getAttendeeAreaPriceResult
const getAttendeeAreaPriceResult = (result) => {
  const currentCurrency = getStoreCurrency();

  let resultOrigin = Object.assign({}, result);
  let eventsList = resultOrigin[currentCurrency]['zeca_yearly'];
  let sessionsList = resultOrigin[currentCurrency]['zecc_yearly'];
  forEach([50, 100, 500, 600, 1000, 3000, 5000, 10000], function (type) {
    eventsList.forEach((ele) => {
      if (type >= ele.start && type < ele.end) {
        resultOrigin[currentCurrency]['zeca_' + type + '_unlimited_monthly'] = ele.price * type;
        resultOrigin[currentCurrency]['zeca_' + type + '_unlimited_yearly'] = ele.price * type;
      }
    });
    sessionsList.forEach((ele) => {
      if (type >= ele.start && type < ele.end) {
        resultOrigin[currentCurrency]['zecc_' + type + '_unlimited_monthly'] = ele.price * type;
        resultOrigin[currentCurrency]['zecc_' + type + '_unlimited_yearly'] = ele.price * type;
      }
    });
  });
  return resultOrigin;
};
export const getUrlLanguageFlag = () => {
  const noChangeLanguages = ['zh-cn', 'zh-tw'];

  if (noChangeLanguages.includes(USER_LANG.toLowerCase())) {
    return USER_LANG.toLowerCase();
  }
  const [prefix] = USER_LANG.split('-');
  return prefix;
};
